import { useState, useEffect } from "react";
import api from "../services/api";

interface Node {
  id: number;
  node_name: string;
  script_id?: number;
  node_group_id?: number;
  messages?: Record<string, string>;
  input_type?: string;
  input_data?: Record<string, any>;
  tracks?: Array<{ type: string; html_element: string }>;
  capture_rules?: Record<string, any>[];
  message_seq_no?: string;
}

interface NodesResponse {
  nodes: Node[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface UseNodesParams {
  page?: number;
  pageSize?: number;
  scriptId?: number;
  search?: string;
  inputType?: string;
  globalOnly?: boolean;
  nodeGroupId?: number;
  fetchAll?: boolean;
}

interface UseNodesReturn {
  nodes: Node[];
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  refreshNodes: () => Promise<void>;
  createNode: (nodeData: Omit<Node, "id">) => Promise<Node>;
  updateNode: (
    id: number,
    nodeData: Partial<Omit<Node, "id">>,
  ) => Promise<Node>;
  deleteNode: (id: number) => Promise<void>;
  cloneNode: (
    id: number,
    newNodeName?: string,
    newScriptId?: number,
  ) => Promise<Node>;
}

export function useNodes(params: UseNodesParams = {}): UseNodesReturn {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(params.page || 1);
  const [pageSize] = useState(params.pageSize || 10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNodes = async () => {
    try {
      setLoading(true);
      setError(null);

      if (params.fetchAll) {
        const searchParams = new URLSearchParams();
        searchParams.append("fetch_all", "true");
        if (params.scriptId)
          searchParams.append("script_id", params.scriptId.toString());
        if (params.nodeGroupId)
          searchParams.append("node_group_id", params.nodeGroupId.toString());
        if (params.search) searchParams.append("search", params.search);
        if (params.inputType)
          searchParams.append("input_type", params.inputType);
        if (params.globalOnly !== undefined)
          searchParams.append("global_only", params.globalOnly.toString());

        const response = await api.get<NodesResponse>(
          `/nodes?${searchParams.toString()}`,
        );
        setNodes(response.data.nodes);
        setTotal(response.data.total);
        setTotalPages(response.data.total_pages);
        setCurrentPage(response.data.page);
        return;
      }

      const searchParams = new URLSearchParams({
        page: (params.page || currentPage).toString(),
        page_size: pageSize.toString(),
      });

      if (params.scriptId) {
        searchParams.append("script_id", params.scriptId.toString());
      }
      if (params.nodeGroupId) {
        searchParams.append("node_group_id", params.nodeGroupId.toString());
      }

      if (params.search) {
        searchParams.append("search", params.search);
      }

      if (params.inputType) {
        searchParams.append("input_type", params.inputType);
      }

      if (params.globalOnly !== undefined) {
        searchParams.append("global_only", params.globalOnly.toString());
      }

      const response = await api.get<NodesResponse>(
        `/nodes?${searchParams.toString()}`,
      );

      setNodes(response.data.nodes);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
      setCurrentPage(response.data.page);
    } catch (err) {
      console.error("❌ Error fetching nodes:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch nodes");
    } finally {
      setLoading(false);
    }
  };

  const refreshNodes = async () => {
    await fetchNodes();
  };

  const createNode = async (nodeData: Omit<Node, "id">): Promise<Node> => {
    try {
      console.log("🏗️ Creating node:", nodeData);
      const response = await api.post<Node>("/nodes", nodeData);
      console.log("✅ Node created:", response);
      await fetchNodes();
      return response.data;
    } catch (err) {
      console.error("❌ Error creating node:", err);
      throw err;
    }
  };

  const updateNode = async (
    id: number,
    nodeData: Partial<Omit<Node, "id">>,
  ): Promise<Node> => {
    try {
      console.log("🔄 Updating node:", id, nodeData);
      const response = await api.put<Node>(`/nodes/${id}`, nodeData);
      console.log("✅ Node updated:", response);
      setNodes((prevNodes) =>
        prevNodes.map((node) => (node.id === id ? response.data : node)),
      );
      return response.data;
    } catch (err) {
      console.error("❌ Error updating node:", err);
      throw err;
    }
  };

  const deleteNode = async (id: number): Promise<void> => {
    try {
      console.log("🗑️ Deleting node:", id);
      await api.delete(`/nodes/${id}`);
      console.log("✅ Node deleted:", id);
      setNodes((prevNodes) => prevNodes.filter((node) => node.id !== id));
      setTotal((prevTotal) => prevTotal - 1);
    } catch (err) {
      console.error("❌ Error deleting node:", err);
      throw err;
    }
  };

  const cloneNode = async (
    id: number,
    newNodeName?: string,
    newScriptId?: number,
  ): Promise<Node> => {
    try {
      console.log("📋 Cloning node:", id, { newNodeName, newScriptId });
      const searchParams = new URLSearchParams();
      if (newNodeName) {
        searchParams.append("new_node_name", newNodeName);
      }
      if (newScriptId !== undefined) {
        searchParams.append("new_script_id", newScriptId.toString());
      }
      const url = `/nodes/${id}/clone${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
      const response = await api.post<Node>(url);
      console.log("✅ Node cloned:", response);
      await fetchNodes();
      return response.data;
    } catch (err) {
      console.error("❌ Error cloning node:", err);
      throw err;
    }
  };

  useEffect(() => {
    fetchNodes();
  }, [
    params.page,
    params.pageSize,
    params.scriptId,
    params.nodeGroupId,
    params.search,
    params.inputType,
    params.globalOnly,
    params.fetchAll,
  ]);

  return {
    nodes,
    total,
    totalPages,
    currentPage,
    pageSize,
    loading,
    error,
    refreshNodes,
    createNode,
    updateNode,
    deleteNode,
    cloneNode,
  };
}
